import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { DropdownTypeFirst, InputField } from '@diamond-design/components';
import CtaButton from '../ctaButton/ctaButton';
import styles from './FormContainer.module.scss';

const getSuggestionLabel = (field, opt) => {
  if (!opt || typeof opt === 'string') return opt ?? '';
  return opt[field.autocompleteDisplayKey] ?? '';
};

const getSuggestionDisplayText = (field, opt) => {
  if (!opt || typeof opt === 'string') return opt ?? '';
  return (field.autocompleteOptionKeys ?? [])
    .map((key) => opt[key])
    .filter(Boolean)
    .join(', ');
};

const FormContainer = forwardRef(function FormContainer(
  {
    title,
    description,
    fields,
    submitButtonLabel,
    onSubmit,
    onAutocomplete,
    onFormValuesChange,
  },
  ref
) {
  const emptyValues = useMemo(
    () => fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
    [fields]
  );

  const [formValues, setFormValues] = useState(emptyValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [autocompleteValues, setAutocompleteValues] = useState({});

  const debounceRef = useRef(null);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const resetForm = () => {
    clearTimeout(debounceRef.current);
    setFormValues(emptyValues);
    setErrors({});
    setAutocompleteSuggestions([]);
    setAutocompleteValues({});
    onFormValuesChange?.(emptyValues);
  };

  useImperativeHandle(ref, () => ({
    reset: resetForm,
  }));

  const validateField = (field, value) => {
    const trimmed = value?.trim() ?? '';

    if (field.required && !trimmed) return `${field.label} is required`;
    if (!trimmed) return '';

    const { minLength, maxLength, pattern } = field.validation ?? {};

    if (minLength && trimmed.length < minLength)
      return `${field.label} must be at least ${minLength} characters`;

    if (maxLength && trimmed.length > maxLength)
      return `${field.label} must be no more than ${maxLength} characters`;

    if (pattern && !pattern.test(trimmed))
      return `${field.label} format is invalid`;

    return '';
  };

  const handleInputChange = ({ target: { name, value } }) => {
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }

    const newValues = { ...formValues, [name]: value };
    setFormValues(newValues);
    onFormValuesChange?.(newValues);
  };

  const handleAutocompleteInputChange = (field, inputValue) => {
    setFormValues((prev) => ({ ...prev, [field.name]: inputValue }));
    setAutocompleteValues((prev) => ({ ...prev, [field.name]: null }));
    setErrors((prev) => {
      if (!prev[field.name]) return prev;
      const copy = { ...prev };
      delete copy[field.name];
      return copy;
    });

    clearTimeout(debounceRef.current);

    if (inputValue.length < 2) {
      setAutocompleteSuggestions([]);
      setAutocompleteLoading(false);
      return;
    }

    setAutocompleteLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const suggestions = (await onAutocomplete?.(field, inputValue)) ?? [];
        setAutocompleteSuggestions(suggestions);
      } catch {
        setAutocompleteSuggestions([]);
      } finally {
        setAutocompleteLoading(false);
      }
    }, 300);
  };

  const handleAutocompleteSelect = (field, suggestion) => {
    if (!suggestion || typeof suggestion === 'string') return;

    const fieldMap = field.autocompleteFieldMap ?? {};
    const updates = {};

    Object.entries(fieldMap).forEach(([suggestionKey, formFieldName]) => {
      if (suggestion[suggestionKey] !== undefined) {
        updates[formFieldName] = suggestion[suggestionKey];
      }
    });

    const newValues = { ...formValues, ...updates };
    setFormValues(newValues);
    setAutocompleteValues((prev) => ({ ...prev, [field.name]: suggestion }));
    onFormValuesChange?.(newValues);
    setAutocompleteSuggestions([]);
    setErrors((prev) => {
      const copy = { ...prev };
      Object.values(fieldMap).forEach((fieldName) => {
        delete copy[fieldName];
      });
      return copy;
    });
  };

  const validate = () => {
    const newErrors = {};
    fields.forEach((field) => {
      const error = validateField(field, formValues[field.name]);
      if (error) newErrors[field.name] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit?.(formValues);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      {title && <h1 className={styles.pageTitle}>{title}</h1>}
      {description && (
        <div className={styles.pageDescription}>{parse(description)}</div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.fieldsGrid}>
          {fields.map((field) => (
            <div
              key={field.name}
              style={{ gridColumn: `span ${field.colSpan || 3}` }}
            >
              {field.type === 'text' && (
                <InputField
                  id={field.name}
                  name={field.name}
                  label={field.label}
                  type="text"
                  value={formValues[field.name]}
                  onChange={handleInputChange}
                  placeholder={field.placeholder}
                  error={!!errors[field.name]}
                  helperText={errors[field.name]}
                  inputProps={{
                    maxLength: field.maxLength,
                  }}
                />
              )}

              {field.type === 'autocomplete' && (
                <DropdownTypeFirst
                  id={field.name}
                  label={field.label}
                  placeholder={field.placeholder}
                  inputValue={formValues[field.name]}
                  value={autocompleteValues[field.name] ?? null}
                  options={autocompleteSuggestions}
                  getOptions={(opt) => getSuggestionLabel(field, opt)}
                  getOptionText={(opt) => getSuggestionDisplayText(field, opt)}
                  onInputChange={(_, inputVal) =>
                    handleAutocompleteInputChange(field, inputVal)
                  }
                  onChange={(_, selected) =>
                    handleAutocompleteSelect(field, selected)
                  }
                  error={errors[field.name]}
                  loading={autocompleteLoading}
                  allowUserInput
                />
              )}
            </div>
          ))}
        </div>

        <CtaButton
          type="submit"
          disabled={isSubmitting}
          variant="primary"
          size="lg"
          fullWidth
          label={submitButtonLabel}
        />
      </form>
    </div>
  );
});

FormContainer.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'select', 'autocomplete']).isRequired,
      label: PropTypes.string.isRequired,
      required: PropTypes.bool,
      placeholder: PropTypes.string,
      colSpan: PropTypes.number,
      maxLength: PropTypes.number,
      autocompleteDisplayKey: PropTypes.string,
      autocompleteOptionKeys: PropTypes.arrayOf(PropTypes.string),
      autocompleteFieldMap: PropTypes.objectOf(PropTypes.string),
      validation: PropTypes.shape({
        minLength: PropTypes.number,
        maxLength: PropTypes.number,
      }),
    })
  ).isRequired,
  submitButtonLabel: PropTypes.string,
  onSubmit: PropTypes.func,
  onAutocomplete: PropTypes.func,
  onFormValuesChange: PropTypes.func,
};

export default FormContainer;
